<template>
  <BContainer
    v-if="currentUser"
    class="mb-4"
  >
    <div class="d-flex justify-content-end">
      <BBreadcrumb
        v-if="$route.meta.breadcrumb"
        :items="$route.meta.breadcrumb || []"
      />
    </div>

    <AdminPageHeader
      title="Students"
      subtitle="Manage student records and information"
      icon="fas fa-user-graduate"
      gradient="green"
    />

    <BRow class="py-2">
      <BCol
        cols="12"
        md="9"
      >
        <BFormGroup
          label="Search"
          label-for="filterSearch"
          label-class="font-weight-bolder"
          class="mb-2"
        >
          <BFormInput
            id="filterSearch"
            v-model="tableStudents.filter.search"
            placeholder="search by ID number, name, email..."
            autocomplete="off"
            debounce="1000"
            type="text"
          />
        </BFormGroup>
      </BCol>

      <BCol
        cols="12"
        md="3"
      >
        <BFormGroup
          label="Status"
          label-for="filterActive"
          label-class="font-weight-bolder"
          class="mb-2"
        >
          <VSelect
            v-model="tableStudents.selected.active"
            placeholder="filter status"
            input-id="filterActive"
            label="name"
            clearable
            append-to-body
            :options="activeOptions"
            :disabled="tableStudents.busy"
            :calculate-position="calculateDropPosition"
          >
            <template #option="{ name }">
              <div
                class="text-break text-wrap"
                v-text="name"
              />
            </template>
            <template #no-options="">
              no available status
            </template>
          </VSelect>
        </BFormGroup>
      </BCol>

      <BCol cols="12">
        <BButton
          size="sm"
          type="button"
          variant="success"
          @click.prevent="createStudent"
        >
          <i class="fas fa-plus mr-1" /> Add Student
        </BButton>
      </BCol>
    </BRow>

    <CustomTable
      ref="tableStudents"
      :provider="tableProvider"
      :fields="tableStudents.fields"
      :filter="tableStudents.filter"
      :filter-on="tableStudents.filterOn"
      :sort-by="tableStudents.sortBy"
      :sort-desc="tableStudents.sortDesc"
      :sort-direction="tableStudents.sortDirection"
      :total-rows="tableStudents.totalRows"
      :current-page="tableStudents.currentPage"
      :per-page="tableStudents.perPage"
      :stacked="tableStudents.stacked"
      :busy="tableStudents.busy"
    >
      <template #cell(action)="{ item }">
        <div class="text-md-nowrap d-flex flex-column flex-md-row justify-content-center gap-2">
          <BButton
            size="sm"
            variant="primary"
            :disabled="tableStudents.busy"
            @click.prevent="editStudent(item)"
          >
            Edit
          </BButton>
          <BButton
            size="sm"
            variant="danger"
            :disabled="tableStudents.busy"
            @click.prevent="deleteStudentConfirm(item)"
          >
            Delete
          </BButton>
        </div>
      </template>

      <template #cell(first_name)="{ item }">
        {{ getFullName(item) }}
      </template>

      <template #cell(active)="{ item }">
        <BBadge :variant="item.active ? 'success' : 'secondary'">
          {{ item.active ? 'Active' : 'Inactive' }}
        </BBadge>
      </template>
    </CustomTable>

    <BModal
      ref="modalStudent"
      size="lg"
      scrollable
      centered
      no-close-on-esc
      no-enforce-focus
      no-close-on-backdrop
      aria-hidden="false"
      role="dialog"
      :title="MStudentTitle"
    >
      <ValidationObserver ref="formStudent">
        <form
          role="form"
          novalidate
          @submit.prevent
        >
          <BRow>
            <BCol
              cols="12"
              md="6"
            >
              <ValidationProvider
                v-slot="{ errors }"
                vid="first_name"
                name="first name"
                rules="required|max:255"
              >
                <BFormGroup
                  label="First Name"
                  label-for="fFirstName"
                  label-class="font-weight-bolder"
                  class="mb-2"
                >
                  <BFormInput
                    id="fFirstName"
                    v-model="formStudent.first_name"
                    placeholder="enter first name here"
                    maxlength="255"
                    autocomplete="off"
                    :state="errors.length > 0 ? false : null"
                    :disabled="state.busy"
                  />
                  <div
                    v-if="errors.length > 0"
                    class="invalid-feedback"
                    v-text="errors[0]"
                  />
                </BFormGroup>
              </ValidationProvider>
            </BCol>

            <BCol
              cols="12"
              md="6"
            >
              <ValidationProvider
                v-slot="{ errors }"
                vid="last_name"
                name="last name"
                rules="required|max:255"
              >
                <BFormGroup
                  label="Last Name"
                  label-for="fLastName"
                  label-class="font-weight-bolder"
                  class="mb-2"
                >
                  <BFormInput
                    id="fLastName"
                    v-model="formStudent.last_name"
                    placeholder="enter last name here"
                    maxlength="255"
                    autocomplete="off"
                    :state="errors.length > 0 ? false : null"
                    :disabled="state.busy"
                  />
                  <div
                    v-if="errors.length > 0"
                    class="invalid-feedback"
                    v-text="errors[0]"
                  />
                </BFormGroup>
              </ValidationProvider>
            </BCol>

            <BCol
              cols="12"
              md="6"
            >
              <ValidationProvider
                v-slot="{ errors }"
                vid="middle_name"
                name="middle name"
                rules="max:255"
              >
                <BFormGroup
                  label="Middle Name"
                  label-for="fMiddleName"
                  label-class="font-weight-bolder"
                  class="mb-2"
                >
                  <BFormInput
                    id="fMiddleName"
                    v-model="formStudent.middle_name"
                    placeholder="enter middle name here"
                    maxlength="255"
                    autocomplete="off"
                    :state="errors.length > 0 ? false : null"
                    :disabled="state.busy"
                  />
                  <div
                    v-if="errors.length > 0"
                    class="invalid-feedback"
                    v-text="errors[0]"
                  />
                </BFormGroup>
              </ValidationProvider>
            </BCol>

            <BCol
              cols="12"
              md="6"
            >
              <ValidationProvider
                v-slot="{ errors }"
                vid="extension_name"
                name="extension name"
                rules="max:255"
              >
                <BFormGroup
                  label="Extension Name"
                  label-for="fExtensionName"
                  label-class="font-weight-bolder"
                  description="e.g. Jr., Sr., III"
                  class="mb-2"
                >
                  <BFormInput
                    id="fExtensionName"
                    v-model="formStudent.extension_name"
                    placeholder="enter extension name here"
                    maxlength="255"
                    autocomplete="off"
                    :state="errors.length > 0 ? false : null"
                    :disabled="state.busy"
                  />
                  <div
                    v-if="errors.length > 0"
                    class="invalid-feedback"
                    v-text="errors[0]"
                  />
                </BFormGroup>
              </ValidationProvider>
            </BCol>

            <BCol
              cols="12"
              md="6"
            >
              <ValidationProvider
                v-slot="{ errors }"
                vid="id_number"
                name="ID number"
                rules="required"
              >
                <BFormGroup
                  label="ID Number"
                  label-for="fIdNumber"
                  label-class="font-weight-bolder"
                  class="mb-2"
                >
                  <BFormInput
                    id="fIdNumber"
                    v-model="formStudent.id_number"
                    placeholder="enter ID number here"
                    autocomplete="off"
                    :state="errors.length > 0 ? false : null"
                    :disabled="state.busy"
                  />
                  <div
                    v-if="errors.length > 0"
                    class="invalid-feedback"
                    v-text="errors[0]"
                  />
                </BFormGroup>
              </ValidationProvider>
            </BCol>

            <BCol
              cols="12"
              md="6"
            >
              <ValidationProvider
                v-slot="{ errors }"
                vid="email"
                name="email"
                rules="required|email"
              >
                <BFormGroup
                  label="Email"
                  label-for="fEmail"
                  label-class="font-weight-bolder"
                  class="mb-2"
                >
                  <BFormInput
                    id="fEmail"
                    v-model="formStudent.email"
                    placeholder="enter email here"
                    autocomplete="off"
                    type="email"
                    :state="errors.length > 0 ? false : null"
                    :disabled="state.busy"
                  />
                  <div
                    v-if="errors.length > 0"
                    class="invalid-feedback"
                    v-text="errors[0]"
                  />
                </BFormGroup>
              </ValidationProvider>
            </BCol>

            <BCol
              cols="12"
              md="6"
            >
              <ValidationProvider
                v-slot="{ errors }"
                vid="contact_number"
                name="contact number"
                rules="max:20"
              >
                <BFormGroup
                  label="Contact Number"
                  label-for="fContactNumber"
                  label-class="font-weight-bolder"
                  class="mb-2"
                >
                  <BFormInput
                    id="fContactNumber"
                    v-model="formStudent.contact_number"
                    placeholder="enter contact number here"
                    maxlength="20"
                    :state="errors.length > 0 ? false : null"
                    :disabled="state.busy"
                  />
                  <div
                    v-if="errors.length > 0"
                    class="invalid-feedback"
                    v-text="errors[0]"
                  />
                </BFormGroup>
              </ValidationProvider>
            </BCol>

            <BCol
              cols="12"
              md="6"
            >
              <ValidationProvider
                v-slot="{ errors }"
                vid="course"
                name="course"
                rules="required|max:255"
              >
                <BFormGroup
                  label="Course"
                  label-for="fCourse"
                  label-class="font-weight-bolder"
                  class="mb-2"
                >
                  <BFormInput
                    id="fCourse"
                    v-model="formStudent.course"
                    placeholder="e.g. BSIT, BSCS"
                    maxlength="255"
                    autocomplete="off"
                    :state="errors.length > 0 ? false : null"
                    :disabled="state.busy"
                  />
                  <div
                    v-if="errors.length > 0"
                    class="invalid-feedback"
                    v-text="errors[0]"
                  />
                </BFormGroup>
              </ValidationProvider>
            </BCol>

            <BCol
              cols="12"
              md="6"
            >
              <ValidationProvider
                v-slot="{ errors }"
                vid="year_level"
                name="year level"
                rules="required"
              >
                <BFormGroup
                  label="Year Level"
                  label-for="fYearLevel"
                  label-class="font-weight-bolder"
                  class="mb-2"
                >
                  <BFormInput
                    id="fYearLevel"
                    v-model="formStudent.year_level"
                    placeholder="enter year level (1-5)"
                    type="number"
                    min="1"
                    max="5"
                    autocomplete="off"
                    :state="errors.length > 0 ? false : null"
                    :disabled="state.busy"
                  />
                  <div
                    v-if="errors.length > 0"
                    class="invalid-feedback"
                    v-text="errors[0]"
                  />
                </BFormGroup>
              </ValidationProvider>
            </BCol>

            <BCol
              v-if="state.editing"
              cols="12"
            >
              <ValidationProvider
                v-slot="{ errors }"
                vid="password"
                name="password"
                rules="min:8"
              >
                <BFormGroup
                  label="New Password (Optional)"
                  label-for="fPassword"
                  label-class="font-weight-bolder"
                  description="Leave blank to keep current password"
                  class="mb-2"
                >
                  <BFormInput
                    id="fPassword"
                    v-model="formStudent.password"
                    placeholder="enter new password here (optional)"
                    autocomplete="off"
                    maxlength="50"
                    type="password"
                    :state="errors.length > 0 ? false : null"
                    :disabled="state.busy"
                  />
                  <div
                    v-if="errors.length > 0"
                    class="invalid-feedback"
                    v-text="errors[0]"
                  />
                </BFormGroup>
              </ValidationProvider>
            </BCol>

            <BCol
              v-if="state.editing"
              cols="12"
            >
              <BFormGroup class="mb-2">
                <BFormCheckbox
                  id="is_active"
                  v-model="formStudent.active"
                  switch
                  value="1"
                  name="is_active"
                  unchecked-value="0"
                  :disabled="state.busy"
                >
                  Is Active?
                </BFormCheckbox>
              </BFormGroup>
            </BCol>
          </BRow>
        </form>
      </ValidationObserver>

      <template #modal-footer="{ cancel }">
        <BButton
          variant="success"
          :disabled="state.busy"
          @click.prevent="validateStudent"
        >
          {{ state.editing ? 'Update' : 'Create' }} Student
        </BButton>
        <BButton
          variant="outline-dark"
          :disabled="state.busy"
          @click.prevent="discardBtnEvent => preventAriaHidden(discardBtnEvent, cancel)"
        >
          Discard
        </BButton>
      </template>
    </BModal>
  </BContainer>
</template>

<script>
import { mapGetters } from 'vuex'

import MxMisc from '@/mixins/misc'
import MxFormatter from '@/mixins/formatter'

import AdminPageHeader from '@/components/core/AdminPageHeader.vue'
import { SAdminStudent } from '@/services'

export default {
  name: 'VAdminStudents',

  components: {
    AdminPageHeader
  },

  mixins: [
    MxMisc,
    MxFormatter
  ],

  middleware: [
    'auth',
    'admin'
  ],

  metaInfo: () => ({
    title: 'Students'
  }),

  data () {
    return {
      state: {
        busy: false,
        editing: false
      },
      activeOptions: [
        { id: 1, name: 'Active' },
        { id: 0, name: 'Inactive' }
      ],
      formStudent: {
        id: '',
        first_name: '',
        middle_name: '',
        last_name: '',
        extension_name: '',
        id_number: '',
        course: '',
        year_level: '',
        email: '',
        contact_number: '',
        password: '',
        active: 1
      },
      tableStudents: {
        busy: false,
        selected: {
          active: null
        },
        filter: {
          search: '',
          active: ''
        },
        filterOn: [],
        stacked: this.isMobile(),
        sortBy: 'id',
        sortDesc: false,
        sortDirection: 'asc',
        pageOptions: [10, 25, 50, 100],
        currentPage: 1,
        totalRows: 0,
        perPage: 10,
        fields: [
          { mobile: 0, key: 'index', label: '#' },
          { mobile: 12, key: 'action', class: 'w-md-125px' },
          { mobile: 1, key: 'id_number', label: 'ID Number', class: 'min-w-100px', sortable: true },
          { mobile: 2, key: 'first_name', label: 'Name', class: 'min-w-175px' },
          { mobile: 3, key: 'course', class: 'min-w-80px', sortable: true },
          { mobile: 4, key: 'year_level', label: 'Year', class: 'min-w-60px', sortable: true },
          { mobile: 5, key: 'email', class: 'min-w-175px' },
          { mobile: 6, key: 'contact_number', label: 'Contact', class: 'min-w-120px' },
          { mobile: 7, key: 'active', class: 'w-md-75px' },
          { mobile: 8, key: 'updated_at', class: 'min-w-150px', formatter: this.dateTimeShortFormatter, sortable: true }
        ].sort(this.scaleOnMobile)
      }
    }
  },

  computed: Object.assign({
    MStudentTitle () {
      return this.state.editing ? 'Edit Student' : 'Add Student'
    }
  }, mapGetters({
    currentUser: 'auth/user'
  })),

  watch: {
    'tableStudents.selected.active' (active) {
      this.tableStudents.filter.active = active !== null && active !== undefined ? active.id : ''
    }
  },

  methods: {

    getFullName (item) {
      let name = item.first_name || ''
      if (item.middle_name) name += ` ${item.middle_name}`
      if (item.last_name) name += ` ${item.last_name}`
      if (item.extension_name) name += ` ${item.extension_name}`
      return name.trim()
    },

    async tableProvider ({ currentPage, perPage, sortBy, sortDesc, filter }) {
      this.tableStudents.busy = true

      const payload = {
        page: currentPage,
        per_page: perPage,
        sort: sortBy,
        sort_desc: sortDesc,
        filter_text: filter.search,
        active: filter.active
      }

      return await SAdminStudent.getList(payload).then(
        ({ data }) => {
          this.tableStudents.totalRows = data.total_rows
          return data.items
        }
      ).catch(() => {
        this.tableStudents.totalRows = 0
        return []
      }).finally(() => {
        this.tableStudents.busy = false
      })
    },

    createStudent () {
      this.state.editing = false

      this.formStudent = Object.assign(this.formStudent, {
        id: '',
        first_name: '',
        middle_name: '',
        last_name: '',
        extension_name: '',
        id_number: '',
        course: '',
        year_level: '',
        email: '',
        contact_number: '',
        password: '',
        active: 1
      })

      this.$refs.modalStudent.show()
    },

    editStudent (student) {
      this.state.editing = true

      this.formStudent = Object.assign(this.formStudent, {
        id: student.id,
        first_name: student.first_name,
        middle_name: student.middle_name,
        last_name: student.last_name,
        extension_name: student.extension_name,
        id_number: student.id_number,
        course: student.course,
        year_level: student.year_level,
        email: student.email,
        contact_number: student.contact_number,
        password: '',
        active: student.active
      })

      this.$refs.modalStudent.show()
    },

    async validateStudent () {
      await this.$refs.formStudent.validate().then(
        async validForm => {
          if (!validForm) {
            return this.swalInvalid()
          }

          this.swalConfirm({
            html: this.state.editing ? '<h6>Update this Student?</h6>' : '<h6>Create this Student?</h6>',
            confirmButtonText: this.state.editing ? 'Update' : 'Create',
            preConfirm: async () => {
              this.state.busy = true

              const createOrUpdate = async () => {
                return this.state.editing ? this.putStudent() : this.postStudent()
              }

              return createOrUpdate().catch(
                ({ message }) => {
                  if (message && message?.errors) {
                    this.$refs.formStudent.setErrors(message.errors)
                  }
                }
              ).finally(() => {
                this.state.busy = false
              })
            }
          })
        }
      )
    },

    async postStudent () {
      return await SAdminStudent.store(this.formStudent).then(
        ({ data: { message } }) => {
          this.swalSuccess(message).then(() => {
            this.$refs.modalStudent.hide()
            this.$refs.tableStudents.refetch()
          })
        }
      )
    },

    async putStudent () {
      return await SAdminStudent.update(this.formStudent.id, this.formStudent).then(
        ({ data: { message } }) => {
          this.swalSuccess(message).then(() => {
            this.$refs.modalStudent.hide()
            this.$refs.tableStudents.refetch()
          })
        }
      )
    },

    async deleteStudentConfirm (student) {
      const result = await this.$swal({
        title: 'Delete Student',
        text: `Are you sure you want to delete ${student.first_name} ${student.last_name}?`,
        icon: 'warning',
        showCancelButton: true,
        confirmButtonColor: '#d33',
        confirmButtonText: 'Yes, delete it!'
      })

      if (result.isConfirmed) {
        this.deleteStudent(student.id)
      }
    },

    async deleteStudent (id) {
      this.state.busy = true
      try {
        await SAdminStudent.destroy(id)
        this.swalSuccess('Student deleted successfully')
        this.$refs.tableStudents.refetch()
      } catch (error) {
        const message = error.response?.data?.message || 'Failed to delete student'
        this.swalError(message)
      } finally {
        this.state.busy = false
      }
    }
  }
}
</script>
